import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ValidationService } from '../validation/validation.service';
import { CampaignChoice, Profile, CampaignStockGroup } from '../../services/campaign.service';

@Component({
  selector: 'app-profile-selection',
  templateUrl: './profile-selection.component.html',
  styleUrls: ['./profile-selection.component.scss', './profile-selection-additional.scss']
})
export class ProfileSelectionComponent implements OnInit, OnChanges {
  @Input() profiles: Profile[] = [];
  @Input() options: CampaignChoice[] = [];
  @Input() stockGroups: CampaignStockGroup[] = [];
  @Input() existingUserChoices: any[] | undefined = undefined;
  @Output() selectionChange = new EventEmitter<any>();

  form: FormGroup;
  errors: { [key: string]: string } = {};

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({});
  }

  ngOnInit() {
    this.initializeForm();
  }

  ngOnChanges() {
    if (this.profiles && this.profiles.length > 0) {
      this.initializeForm();
    } else {
      // Réinitialiser si pas de profils
      this.form = this.fb.group({});
    }
  }

  private initializeForm() {
    const formControls: { [key: string]: any } = {};
    
    this.profiles.forEach((profile, index) => {
      formControls[`profile_${index}`] = this.fb.control(profile.selectedOption || null);
    });

    this.form = this.fb.group(formControls);
  }


  getProfileError(profileId: number): string {
    return this.errors[`profile${profileId}`] || '';
  }

  hasProfileError(profileId: number): boolean {
    return !!this.errors[`profile${profileId}`];
  }

  onOptionChange(profileIndex: number, optionId: number | null) {
    const profileControl = this.form.get(`profile_${profileIndex}`);
    
    if (!profileControl) {
      console.error(`Profile control not found for index ${profileIndex}`);
      return;
    }
    
    profileControl.patchValue(optionId);
    
    // Mettre à jour le profil local
    this.profiles[profileIndex].selectedOption = optionId;
    
    // Émettre le changement
    this.selectionChange.emit(this.form.value);
    
    // Effacer l'erreur si elle existe
    if (this.errors[`profile${this.profiles[profileIndex].id}`]) {
      delete this.errors[`profile${this.profiles[profileIndex].id}`];
    }
  }

  validateForm(): boolean {
    this.errors = {};
    
    // Vérifier qu'au moins un membre a été sélectionné avec une vraie option (pas "Aucune sélection")
    // OU qu'au moins un profil a déjà souscrit
    const hasAtLeastOneSelection = this.profiles.some((profile, index) => {
      // Si le profil a déjà souscrit, c'est valide
      if (this.hasProfileAlreadySubscribed(profile.id)) {
        return true;
      }
      
      const control = this.form.get(`profile_${index}`);
      const value = control?.value;
      // Une sélection valide est une valeur qui n'est pas null (donc pas "Aucune sélection")
      return control && value !== null && value !== undefined;
    });
    
    if (!hasAtLeastOneSelection) {
      // Afficher un message d'erreur général
      this.errors['general'] = 'Veuillez sélectionner au moins un membre pour participer';
      return false;
    }
    
    return true;
  }

  getFormValue() {
    return this.form.value;
  }

  resetForm() {
    this.form.reset();
    this.errors = {};
  }

  getAvailableOptions(profile: Profile): CampaignChoice[] {
    // Retourner toutes les options car le filtrage est déjà fait dans campaign-page.component.ts
    return this.options;
  }

  // Un socio a déjà une brique simple sur le mur, et le palier actuellement sélectionné pour lui
  // donne droit au message : c'est le seul cas qui complète une brique déjà validée (cas 3, cf.
  // texte de la campagne : "il est possible d'ajouter un message avec le palier à 38€").
  addsPhraseToExistingBrick(profile: Profile): boolean {
    if (profile.plaqueType !== 'simple' || !profile.selectedOption) {
      return false;
    }
    const choice = this.options.find(o => o.id === profile.selectedOption);
    return !!choice?.grants_wall_message;
  }

  getStockInfo(option: CampaignChoice): { name: string; remaining: number | null; isShared: boolean } {
    if (option.stock_group_id && this.stockGroups.length > 0) {
      const stockGroup = this.stockGroups.find(group => group.id === option.stock_group_id);
      if (stockGroup) {
        return {
          name: stockGroup.name,
          remaining: stockGroup.remaining_quantity,
          isShared: true
        };
      }
    }

    // Fallback sur l'ancien système
    return {
      name: 'Stock individuel',
      remaining: option.remaining_quantity || null,
      isShared: false
    };
  }

  // Vérifier si un profil a déjà souscrit
  hasProfileAlreadySubscribed(profileId: number): boolean {
    if (!this.existingUserChoices || this.existingUserChoices.length === 0) {
      return false;
    }

    const hasSubscribed = this.existingUserChoices.some(order => {
      if (order.socios_choices) {
        return order.socios_choices.some((choice: any) => {
          // Convertir les deux en number pour la comparaison
          const choiceSocioId = parseInt(choice.socioId);
          const profileIdNum = parseInt(profileId.toString());
          return choiceSocioId === profileIdNum && choice.selectedChoices && choice.selectedChoices.length > 0;
        });
      }
      return false;
    });
    
    return hasSubscribed;
  }

  // Récupérer les choix existants pour un profil
  getExistingChoicesForProfile(profileId: number): any[] {
    if (!this.existingUserChoices || this.existingUserChoices.length === 0) {
      return [];
    }

    const choices: any[] = [];
    this.existingUserChoices.forEach(order => {
      if (order.socios_choices) {
        order.socios_choices.forEach((socioChoice: any) => {
          // Convertir les deux en number pour la comparaison
          const choiceSocioId = parseInt(socioChoice.socioId);
          const profileIdNum = parseInt(profileId.toString());
          if (choiceSocioId === profileIdNum && socioChoice.selectedChoices) {
            socioChoice.selectedChoices.forEach((choice: any) => {
              choices.push({
                choiceName: choice.choiceName,
                totalPrice: choice.totalPrice,
                quantity: choice.quantity
              });
            });
          }
        });
      }
    });

    return choices;
  }
} 